import { getStoredFilePath, resolveStoredFileUrl } from "utils/storedFileUrl";
import PropTypes from "prop-types";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Card,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  Grid,
  Icon,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";
import { gql, useLazyQuery, useMutation, useQuery, useSubscription } from "@apollo/client";
import dayjs from "dayjs";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import SoftTypography from "components/SoftTypography";
import CustomPagination from "components/Pagination";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import { ConfirmarContext } from "context/ConfirmarContext";
import { MessageManager } from "context";
import { MenuContext } from "context/menuContext";
import { UserContext } from "context/user";
import { getUproColor } from "utils/colors";
import { valorAPesos } from "utils/formatters";
import FacturaImpresion from "layouts/pedidos/components/pedidos/FacturaImpresion";
import FileHistorial from "layouts/PDF/clientes/cargaSaldos";
import pdfIcon from "assets/images/file-type-pdf2.svg";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "95%",
  width: { xs: "96%", md: "92%", xl: "88%" },
  maxWidth: "1600px",
  overflowY: "auto",
  p: 3,
};

const CC_QUERY = gql`
  query cuentaCorrienteInfo(
    $search: String
    $id: Int
    $idCliente: Int
    $filter: JSON
    $idSedeUser: Int
  ) {
    cuentaCorrienteInfo(
      search: $search
      id: $id
      idCliente: $idCliente
      filter: $filter
      idSedeUser: $idSedeUser
    ) {
      __typename
      id
      idCliente
      total
      createdAt
      tipo
      idVenta
      cliente {
        id
        nombre
        apellido
        cuentaUpro
      }
      ... on CuentaCorriente {
        idPedido
        descripcion
        segmentoCuentaCorriente
        detalles {
          id
          cantidad
          precio
          producto {
            nombre
          }
        }
      }
      ... on PagoCuentaCorriente {
        estado
        medioPago
        motivoCancelacion
        descripcion
        fechaPago
        file
        nroTransferencia
        banco
        segmentoCuentaCorriente
        esCreditoHistorial
        tipoCargaHistorial
        idVenta
        historialCargaSaldoGrupo {
          fechaPago
          tipoCarga
          totalPago
          pagos {
            id
            idCliente
            cliente {
              id
              nombre
              apellido
              dni
            }
          }
        }
      }
    }
  }
`;

const CC_TOTALS_QUERY = gql`
  query cuentaCorrienteInfoResumen(
    $search: String
    $id: Int
    $idCliente: Int
    $filter: JSON
    $idSedeUser: Int
  ) {
    cuentaCorrienteInfoResumen(
      search: $search
      id: $id
      idCliente: $idCliente
      filter: $filter
      idSedeUser: $idSedeUser
    ) {
      totalDeudaPeriodo
      totalPagosAprobadosPeriodo
      totalBecaSaldoRecibidoPeriodo
      saldoPeriodo
      saldoPeriodoNeto
      saldoInicioPeriodo
      saldoInicioPeriodoNeto
      saldoTotal
      saldoTotalNeto
      cantidadPagosPendientesTransferencia
      cantidadPagosRechazadosTransferencia
    }
  }
`;

const SAVE_PEDIDO_SEGMENTO = gql`
  mutation savePedidoSegmento($input: PedidoInput!) {
    savePedido(input: $input) {
      id
    }
  }
`;

const SAVE_PAGO_SEGMENTO = gql`
  mutation savePagoSegmento($input: PagoCuentaCorrienteInput!) {
    savePagoCuentaCorriente(input: $input) {
      id
      segmentoCuentaCorriente
    }
  }
`;

const SAVE_PAGO_ESTADO = gql`
  mutation savePagoEstado($input: PagoCuentaCorrienteInput!) {
    savePagoCuentaCorriente(input: $input) {
      id
      estado
      descripcion
      motivoCancelacion
      total
    }
  }
`;

const GET_VENTA = gql`
  query getVenta($id: ID!) {
    venta(id: $id) {
      id
      idAnulada
      idPedido
      cliente {
        nombre
        apellido
        appUsuario
      }
      tipoFactura
      fechaFactura
      tipo_cbte
      tipo_doc
      nro_doc
      imp_iva21
      subtotal21
      concepto
      nroAnulado
      url
      pto_vta
      numeroDeFacturaAfip
      CAE
      CAEFchVto
      descuento
      recargo
      total
      mesa
      pedido {
        id
        tipo
        cuentaCorriente
        propina
        detalles {
          producto {
            nombre
          }
          cantidad
          precio
        }
      }
    }
  }
`;

const SEGMENTO_TODOS = "TODOS";
const SEGMENTO_FUNCIONARIO = "FUNCIONARIO";
const SEGMENTO_NO_FUNCIONARIO = "NO_FUNCIONARIO";
const MODO_SALDO_ACUMULADO = "SALDO_ACUMULADO";
const MODO_SALDO_PERIODO = "SALDO_PERIODO";
const FECHA_INICIO_ACUMULADO = "2020-01-01";
const FECHA_INICIO_PERIODO_DEFAULT = "2025-01-01";
const MOVIMIENTOS_PAGE_SIZE = 10;

const isImageFile = (filename) => {
  if (!filename) return false;
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
  const lower = String(filename).toLowerCase();
  return imageExtensions.some((ext) => lower.includes(ext));
};

const isTransferencia = (item) => String(item?.medioPago || "").toLowerCase().includes("transfer");

const getSegmentoByCuentaUpro = (cuentaUpro) => (cuentaUpro ? SEGMENTO_NO_FUNCIONARIO : SEGMENTO_FUNCIONARIO);

const getSegmentoActual = (item) => {
  if (item?.segmentoCuentaCorriente) return item.segmentoCuentaCorriente;
  return getSegmentoByCuentaUpro(Boolean(item?.cliente?.cuentaUpro));
};

const getSegmentoTexto = (segmento) => (segmento === SEGMENTO_NO_FUNCIONARIO ? "NO FUNCIONARIO" : "FUNCIONARIO");

const getSegmentoSiguiente = (segmentoActual) =>
  segmentoActual === SEGMENTO_FUNCIONARIO ? SEGMENTO_NO_FUNCIONARIO : SEGMENTO_FUNCIONARIO;

const getPagoFechaTexto = (item) => {
  const fecha = item?.fechaPago || item?.createdAt;
  if (!fecha) return "Sin fecha";
  return dayjs(fecha).format("DD/MM/YYYY");
};

export default function CuentaCorrienteModal({ open, handleClose, selected }) {
  const { user } = useContext(UserContext);
  const { menu } = useContext(MenuContext);
  const { confirmar } = useContext(ConfirmarContext);
  const { handleSnackbar } = useContext(MessageManager);
  const hoy = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const [pedidosPage, setPedidosPage] = useState(1);
  const [pagosPage, setPagosPage] = useState(1);
  const [segmentoFiltro, setSegmentoFiltro] = useState(SEGMENTO_TODOS);
  const [segmentoFiltroDraft, setSegmentoFiltroDraft] = useState(SEGMENTO_TODOS);
  const [fechaInicio, setFechaInicio] = useState(FECHA_INICIO_ACUMULADO);
  const [fechaFin, setFechaFin] = useState(hoy);
  const [fechaInicioDraft, setFechaInicioDraft] = useState(FECHA_INICIO_ACUMULADO);
  const [fechaFinDraft, setFechaFinDraft] = useState(hoy);
  const [modoSaldo, setModoSaldo] = useState(MODO_SALDO_ACUMULADO);
  const [modoSaldoDraft, setModoSaldoDraft] = useState(MODO_SALDO_ACUMULADO);

  const [expandedPedidos, setExpandedPedidos] = useState({});
  const [expandedPagos, setExpandedPagos] = useState({});
  const [montoPagoAprobacion, setMontoPagoAprobacion] = useState({});

  const [openModalFactura, setOpenModalFactura] = useState(false);
  const [facturaData, setFacturaData] = useState(null);
  const [empresaData, setEmpresaData] = useState(null);

  const [previewFile, setPreviewFile] = useState(null);
  const [pdfData, setPdfData] = useState(null);

  const [savePedidoSegmento, { loading: loadingSavePedidoSegmento }] = useMutation(SAVE_PEDIDO_SEGMENTO);
  const [savePagoSegmento, { loading: loadingSavePagoSegmento }] = useMutation(SAVE_PAGO_SEGMENTO);
  const [savePagoEstado, { loading: loadingSavePagoEstado }] = useMutation(SAVE_PAGO_ESTADO);

  const [getVenta] = useLazyQuery(GET_VENTA);

  const commonFilter = useMemo(
    () => ({
      campo: "id",
      direccion: "ASC",
      segmentoCuentaCorriente: segmentoFiltro === SEGMENTO_TODOS ? null : segmentoFiltro,
      fechaInicio,
      fechaFin,
    }),
    [segmentoFiltro, fechaInicio, fechaFin]
  );

  const {
    data: movimientosData,
    loading: loadingMovimientos,
    refetch: refetchMovimientos,
  } = useQuery(CC_QUERY, {
    variables: {
      search: selected?.dni,
      idCliente: selected?.id ? Number(selected.id) : null,
      idSedeUser: user?.sede?.id ? Number(user?.sede?.id) : null,
      filter: { ...commonFilter, incluirTodosLosPedidos: true },
    },
    skip: !open || !selected?.id || !user?.sede?.id,
    fetchPolicy: "no-cache",
    cachePolicy: "no-cache",
  });

  const {
    data: resumenData,
    loading: loadingResumen,
    refetch: refetchResumen,
  } = useQuery(CC_TOTALS_QUERY, {
    variables: {
      search: selected?.dni,
      idCliente: selected?.id ? Number(selected.id) : null,
      idSedeUser: user?.sede?.id ? Number(user?.sede?.id) : null,
      filter: { ...commonFilter, excluirPedidosConPago: true },
    },
    skip: !open || !selected?.id || !user?.sede?.id,
    fetchPolicy: "no-cache",
    cachePolicy: "no-cache",
  });

  const { data: responseCuentaData } = useSubscription(gql`
    subscription OnResponseCuenta($idCliente: ID) {
      responseCuenta(idCliente: $idCliente) {
        id
        idCliente
      }
    }
  `, {
    variables: { idCliente: selected?.id },
    skip: !open || !selected?.id,
  });

  const { data: pagoCuentaData } = useSubscription(gql`
    subscription OnPagoCuenta {
      pagoCuenta {
        id
      }
    }
  `);

  const movimientos = movimientosData?.cuentaCorrienteInfo || [];
  const pedidos = useMemo(
    () =>
      movimientos.filter(
        (item) => item?.__typename === "CuentaCorriente" || item?.tipo === "CuentaCorriente"
      ),
    [movimientos]
  );
  const pagos = useMemo(
    () =>
      movimientos.filter(
        (item) => item?.__typename === "PagoCuentaCorriente" || item?.tipo === "PagoCuentaCorriente"
      ),
    [movimientos]
  );
  const pagosFiltrados = useMemo(
    () =>
      pagos.filter((item) => {
        const estado = item?.estado;
        if (estado === "Pendiente" || estado === "Rechazado") return isTransferencia(item);
        return true;
      }),
    [pagos]
  );

  const pedidosPagination = useMemo(
    () => ({
      pages: Math.max(Math.ceil(pedidos.length / MOVIMIENTOS_PAGE_SIZE), 1),
      total: pedidos.length,
    }),
    [pedidos]
  );
  const pagosPagination = useMemo(
    () => ({
      pages: Math.max(Math.ceil(pagosFiltrados.length / MOVIMIENTOS_PAGE_SIZE), 1),
      total: pagosFiltrados.length,
    }),
    [pagosFiltrados]
  );
  const pedidosPaginados = useMemo(() => {
    const offset = (pedidosPage - 1) * MOVIMIENTOS_PAGE_SIZE;
    return pedidos.slice(offset, offset + MOVIMIENTOS_PAGE_SIZE);
  }, [pedidos, pedidosPage]);
  const pagosPaginados = useMemo(() => {
    const offset = (pagosPage - 1) * MOVIMIENTOS_PAGE_SIZE;
    return pagosFiltrados.slice(offset, offset + MOVIMIENTOS_PAGE_SIZE);
  }, [pagosFiltrados, pagosPage]);

  const refreshColumns = useCallback(() => {
    refetchMovimientos();
    refetchResumen();
  }, [refetchMovimientos, refetchResumen]);

  const resumen = useMemo(
    () => resumenData?.cuentaCorrienteInfoResumen || {},
    [resumenData]
  );

  const totalDeuda = Number(resumen?.totalDeudaPeriodo || 0);
  const totalPagos = Number(resumen?.totalPagosAprobadosPeriodo || 0);
  const totalBecaSaldoRecibido = Number(resumen?.totalBecaSaldoRecibidoPeriodo || 0);
  const saldoPeriodo = Number(resumen?.saldoPeriodoNeto || 0);
  const saldoInicioPeriodo = Number(resumen?.saldoInicioPeriodo || 0);
  const saldoAcumulado = Number(resumen?.saldoTotalNeto || 0);
  const cantidadPagosPendientes = Number(resumen?.cantidadPagosPendientesTransferencia || 0);
  const cantidadPagosRechazados = Number(resumen?.cantidadPagosRechazadosTransferencia || 0);
  const saldoMostrado = modoSaldo === MODO_SALDO_ACUMULADO ? saldoAcumulado : saldoPeriodo;
  const saldoCardMeta = useMemo(() => {
    if (saldoMostrado < 0) {
      return {
        label: "Deuda pendiente",
        color: "#D32F2F",
      };
    }
    if (saldoMostrado > 0) {
      return {
        label: "A favor",
        color: "#2E7D32",
      };
    }
    return {
      label: "Saldado",
      color: "#6B7280",
    };
  }, [saldoMostrado]);

  const handleGenerateHistorialPdf = useCallback((item) => {
    if (!item?.historialCargaSaldoGrupo) return;
    setPdfData(item.historialCargaSaldoGrupo);
  }, []);

  const handlePdfGenerated = useCallback(() => {
    setPdfData(null);
  }, []);

  const limpiarEstados = useCallback(() => {
    setPedidosPage(1);
    setPagosPage(1);
    setSegmentoFiltro(SEGMENTO_TODOS);
    setSegmentoFiltroDraft(SEGMENTO_TODOS);
    setFechaInicio(FECHA_INICIO_ACUMULADO);
    setFechaFin(hoy);
    setFechaInicioDraft(FECHA_INICIO_ACUMULADO);
    setFechaFinDraft(hoy);
    setModoSaldo(MODO_SALDO_ACUMULADO);
    setModoSaldoDraft(MODO_SALDO_ACUMULADO);
    setExpandedPedidos({});
    setExpandedPagos({});
    setMontoPagoAprobacion({});
    setOpenModalFactura(false);
    setFacturaData(null);
    setEmpresaData(null);
    setPreviewFile(null);
    setPdfData(null);
  }, [hoy]);

  const handleCloseModal = () => {
    limpiarEstados();
    handleClose();
  };

  useEffect(() => {
    if (open && selected?.id) {
      limpiarEstados();
    }
  }, [open, selected?.id, limpiarEstados]);

  useEffect(() => {
    if (!open || !selected?.id) return;
    if (
      (responseCuentaData?.responseCuenta?.idCliente != null &&
        String(responseCuentaData.responseCuenta.idCliente) === String(selected.id)) ||
      pagoCuentaData
    ) {
      refreshColumns();
    }
  }, [responseCuentaData, pagoCuentaData, open, selected?.id, refreshColumns]);

  useEffect(() => {
    setPedidosPage(1);
    setPagosPage(1);
    setExpandedPedidos({});
    setExpandedPagos({});
  }, [segmentoFiltro]);

  const handleBuscar = () => {
    const requiereFechaInicio = modoSaldoDraft === MODO_SALDO_PERIODO;
    if ((!fechaFinDraft || (requiereFechaInicio && !fechaInicioDraft))) {
      handleSnackbar(
        requiereFechaInicio
          ? "Debe indicar fecha de inicio y fecha fin"
          : "Debe indicar fecha de corte",
        "warning"
      );
      return;
    }
    if (
      requiereFechaInicio &&
      dayjs(fechaInicioDraft).isAfter(dayjs(fechaFinDraft), "day")
    ) {
      handleSnackbar("La fecha de inicio no puede ser mayor a la fecha fin", "warning");
      return;
    }
    setFechaInicio(
      modoSaldoDraft === MODO_SALDO_ACUMULADO
        ? FECHA_INICIO_ACUMULADO
        : fechaInicioDraft
    );
    setFechaFin(fechaFinDraft);
    setSegmentoFiltro(segmentoFiltroDraft);
    setModoSaldo(modoSaldoDraft);
    setPedidosPage(1);
    setPagosPage(1);
    setExpandedPedidos({});
    setExpandedPagos({});
  };

  const handleChangeModoSaldo = (event) => {
    const nextModoSaldo = event.target.value;
    setModoSaldoDraft(nextModoSaldo);
    if (nextModoSaldo === MODO_SALDO_ACUMULADO) {
      setFechaInicioDraft(FECHA_INICIO_ACUMULADO);
    } else if (fechaInicioDraft === FECHA_INICIO_ACUMULADO) {
      setFechaInicioDraft(FECHA_INICIO_PERIODO_DEFAULT);
    }
  };

  const mostrarFechaInicioPeriodo = modoSaldoDraft === MODO_SALDO_PERIODO;

  const handleOpenFacturaModal = (factura, empresa) => {
    setFacturaData(factura);
    setEmpresaData(empresa);
    setOpenModalFactura(true);
  };

  const handleCloseFacturaModal = () => {
    setOpenModalFactura(false);
    setFacturaData(null);
    setEmpresaData(null);
  };

  const handleOpenFactura = (idVenta) => {
    if (!idVenta) return;
    getVenta({ variables: { id: idVenta } })
      .then((res) => {
        if (res?.data?.venta) {
          handleOpenFacturaModal(res.data.venta, user?.empresa);
        }
      })
      .catch((error) => {
        handleSnackbar(error.message || "No se pudo obtener la factura", "error");
      });
  };

  const handleReclasificar = async (item, tipoItem, nuevoSegmento) => {
    try {
      if (tipoItem === "pedido") {
        await savePedidoSegmento({
          variables: {
            input: {
              id: item.idPedido || item.id,
              segmentoCuentaCorriente: nuevoSegmento,
            },
          },
        });
      } else {
        await savePagoSegmento({
          variables: {
            input: {
              id: item.id,
              segmentoCuentaCorriente: nuevoSegmento,
            },
          },
        });
      }
      handleSnackbar("Segmento actualizado", "success");
      refreshColumns();
    } catch (error) {
      handleSnackbar(error.message || "No se pudo actualizar el segmento", "error");
    }
  };

  const handleOpenSegmentMenu = (event, item, tipoItem) => {
    const segmentoActual = getSegmentoActual(item);
    const nuevoSegmento = getSegmentoSiguiente(segmentoActual);

    menu({
      open: event.currentTarget,
      align: "right",
      options: [
        {
          name: `Clasificar como ${getSegmentoTexto(nuevoSegmento)}`,
          icon: { icon: "swap_horiz", color: getUproColor("uproBlue") },
          onClick: () => {
            confirmar({
              title: "Confirmar cambio",
              message: `Esta seguro que desea cambiar el tipo de cliente en este ${tipoItem}?`,
              func: () => handleReclasificar(item, tipoItem, nuevoSegmento),
            });
          },
        },
      ],
    });
  };

  const togglePedido = (id) => {
    setExpandedPedidos((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const togglePago = (id) => {
    setExpandedPagos((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleChangeMontoAprobacion = (idPago, value) => {
    setMontoPagoAprobacion((prev) => ({ ...prev, [idPago]: value }));
  };

  const handleActualizarEstadoPago = async ({ idPago, estado, total = null, motivoCancelacion = null }) => {
    try {
      await savePagoEstado({
        variables: {
          input: {
            id: idPago,
            estado,
            ...(total !== null ? { total: Number(total) } : {}),
            ...(motivoCancelacion ? { motivoCancelacion } : {}),
          },
        },
      });

      handleSnackbar(`Pago ${estado.toLowerCase()} correctamente`, "success");
      refreshColumns();
    } catch (error) {
      handleSnackbar(error.message || "No se pudo actualizar el pago", "error");
    }
  };

  const handleAprobarPago = (item) => {
    const montoIngresado = Number(montoPagoAprobacion[item?.id]);
    if (!montoIngresado || montoIngresado <= 0) {
      handleSnackbar("Debe indicar un monto válido para aprobar el pago", "warning");
      return;
    }
    handleActualizarEstadoPago({
      idPago: item?.id,
      estado: "Aprobado",
      total: montoIngresado,
    });
  };

  const handleRechazarPago = (item) => {
    const montoIngresado = Number(montoPagoAprobacion[item?.id]);
    confirmar({
      icon: "warning",
      title: "Rechazar pago",
      message: "Indique el motivo del rechazo",
      pedirMotivo: true,
      placeholder: "Detalle el motivo del rechazo",
      func: async (motivo) => {
        await handleActualizarEstadoPago({
          idPago: item?.id,
          estado: "Rechazado",
          ...(montoIngresado > 0 ? { total: montoIngresado } : {}),
          motivoCancelacion: motivo,
        });
      },
    });
  };

  const headerSegmento = (item, tipoItem) => (
    <SoftButton
      color="primary"
      variant="text"
      sx={{ p: 0, minWidth: "auto" }}
    // onClick={(event) => {
    //   event.stopPropagation();
    //   handleOpenSegmentMenu(event, item, tipoItem);
    // }}
    >
      <SoftTypography variant="caption" fontWeight="bold" color="primary">
        {getSegmentoTexto(getSegmentoActual(item))}
      </SoftTypography>
    </SoftButton>
  );

  const renderPedidoCard = (item) => {
    const idPedido = item.idPedido || item.id;
    const isExpanded = Boolean(expandedPedidos[idPedido]);
    const total = Number(item.total || 0);

    return (
      <Card key={`pedido-${idPedido}`} sx={{ p: 2, mb: 1.5, border: "1px solid #e6e6e6" }}>
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
          <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Icon
              color="primary"
              sx={{ cursor: "pointer", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "all 0.2s" }}
              onClick={() => togglePedido(idPedido)}
            >
              play_circle_outline
            </Icon>
            <SoftTypography variant="button" fontWeight="bold">
              #{idPedido}
            </SoftTypography>
            {headerSegmento(item, "pedido")}
            <SoftTypography variant="caption" color="text">
              {dayjs(item.createdAt).format("DD/MM/YYYY HH:mm")}
            </SoftTypography>
          </SoftBox>

          <SoftTypography variant="button" fontWeight="bold" color="dark">
            {valorAPesos(total)}
          </SoftTypography>
        </SoftBox>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 1.5 }} />
          <SoftBox>
            {Array.isArray(item.detalles) && item.detalles.length > 0 ? (
              item.detalles.map((detalle) => {
                const subtotal = Number(detalle?.cantidad || 0) * Number(detalle?.precio || 0);
                return (
                  <SoftBox key={`detalle-${idPedido}-${detalle?.id}`} display="flex" justifyContent="space-between" py={0.5}>
                    <SoftTypography variant="caption">
                      {detalle?.cantidad || 0}x {detalle?.producto?.nombre || "Producto"}
                    </SoftTypography>
                    <SoftTypography variant="caption" fontWeight="medium">
                      {valorAPesos(subtotal)}
                    </SoftTypography>
                  </SoftBox>
                );
              })
            ) : (
              <SoftTypography variant="caption" color="text">
                Sin detalles del pedido
              </SoftTypography>
            )}

            <Divider sx={{ my: 1 }} />
            <SoftBox display="flex" justifyContent="space-between" alignItems="center">
              <SoftTypography variant="button" fontWeight="bold">
                Total
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="bold">
                {valorAPesos(total)}
              </SoftTypography>
            </SoftBox>

            {item?.idVenta ? (
              <SoftBox mt={1.5}>
                <SoftButton color="primary" circular size="small" onClick={() => handleOpenFactura(item.idVenta)}>
                  Ver factura
                </SoftButton>
              </SoftBox>
            ) : null}
          </SoftBox>
        </Collapse>
      </Card>
    );
  };

  const renderPagoCard = (item) => {
    const hasHistorialPdf = Boolean(item?.historialCargaSaldoGrupo);
    const idPago = item.id;
    const isExpanded = Boolean(expandedPagos[idPago]);
    const isPendienteTransferencia = item?.estado === "Pendiente" && isTransferencia(item);
    const isRechazadoTransferencia = item?.estado === "Rechazado" && isTransferencia(item);
    const isFuncionario = getSegmentoActual(item) === SEGMENTO_FUNCIONARIO;
    const primerComprobante =
      getStoredFilePath(item?.comprobanteFile) || getStoredFilePath(item?.file);
    const fileUrl = resolveStoredFileUrl(primerComprobante);
    const backgroundColor = isPendienteTransferencia
      ? "#FFF3E0"
      : isRechazadoTransferencia
        ? "#FFEBEE"
        : "#FFFFFF";

    return (
      <Card key={`pago-${idPago}`} sx={{ p: 2, mb: 1.5, border: "1px solid #e6e6e6", backgroundColor }}>
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
          <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Icon
              color="primary"
              sx={{ cursor: "pointer", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", transition: "all 0.2s" }}
              onClick={() => togglePago(idPago)}
            >
              play_circle_outline
            </Icon>
            <SoftTypography variant="button" fontWeight="bold">
              #{idPago}
            </SoftTypography>
            {headerSegmento(item, "pago")}
            {item?.esCreditoHistorial ? (
              <SoftTypography variant="caption" fontWeight="bold" color="info">
                {item?.tipoCargaHistorial || "Beca/Saldo"}
              </SoftTypography>
            ) : null}
            {item?.medioPago ? (
              <SoftTypography variant="caption" fontWeight="bold" color="dark">
                {item.medioPago}
              </SoftTypography>
            ) : null}
            <SoftTypography variant="caption" color="text">
              {getPagoFechaTexto(item)}
            </SoftTypography>
          </SoftBox>

        </SoftBox>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 1.5 }} />
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <SoftTypography variant="caption" fontWeight="bold">
                Estado: {item?.estado || "Sin estado"}
              </SoftTypography>
              {item?.esCreditoHistorial && hasHistorialPdf ? (
                <SoftButton
                  variant="text"
                  color="dark"
                  size="small"
                  onClick={() => handleGenerateHistorialPdf(item)}
                  sx={{ minWidth: "auto", p: 0.3 }}
                  title="Descargar resumen de carga"
                >
                  <SoftBox
                    component="img"
                    src={pdfIcon}
                    alt="Descargar resumen"
                    sx={{ width: 30, height: 30, objectFit: "contain" }}
                  />
                </SoftButton>
              ) : null}
            </SoftBox>

            {isFuncionario ? (
              <SoftBox mt={1.5}>
                <Card
                  sx={{
                    border: "1px solid #e6e6e6",
                    p: 1.5,
                    cursor: fileUrl ? "pointer" : "default",
                    mb: 1.5,
                  }}
                  onClick={() => {
                    if (fileUrl) setPreviewFile(primerComprobante);
                  }}
                >
                  {fileUrl ? (
                    isImageFile(primerComprobante) ? (
                      <img
                        src={fileUrl}
                        alt="comprobante"
                        style={{ width: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 8 }}
                      />
                    ) : (
                      <SoftBox
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        minHeight={120}
                      >
                        <Icon color="primary" sx={{ mb: 1 }}>
                          picture_as_pdf
                        </Icon>
                        <SoftTypography variant="caption" color="text">
                          Ver comprobante
                        </SoftTypography>
                      </SoftBox>
                    )
                  ) : (
                    <SoftBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      minHeight={120}
                    >
                      <Icon color="secondary" sx={{ mb: 1 }}>
                        image_not_supported
                      </Icon>
                      <SoftTypography variant="caption" color="text">
                        Sin comprobante adjunto
                      </SoftTypography>
                    </SoftBox>
                  )}
                </Card>
              </SoftBox>
            ) : (
              <>
                <SoftBox mt={1.5}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Fecha de creación
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                    {item?.createdAt ? dayjs(item.createdAt).format("DD/MM/YYYY") : "Sin fecha informada"}
                  </SoftTypography>
                </SoftBox>

                <SoftBox mt={1.5}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Concepto
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                    {item?.descripcion || "Sin concepto informado"}
                  </SoftTypography>
                </SoftBox>

                <SoftBox mt={1.5}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Monto del pago
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                    {valorAPesos(Math.abs(Number(item?.total || 0)))}
                  </SoftTypography>
                </SoftBox>

                <SoftBox mt={1.5}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Nro. de transferencia
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                    {item?.nroTransferencia || "Sin número informado"}
                  </SoftTypography>
                </SoftBox>

                <SoftBox mt={1.5}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Banco
                  </SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                    {item?.banco || "Sin banco informado"}
                  </SoftTypography>
                </SoftBox>

                {isRechazadoTransferencia ? (
                  <SoftBox mt={1.5}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Motivo de rechazo
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
                      {item?.descripcion || "Sin motivo informado"}
                    </SoftTypography>
                  </SoftBox>
                ) : null}
              </>
            )}

            {isPendienteTransferencia ? (
              <SoftBox mt={1.5}>
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Monto a aprobar
                </SoftTypography>
                <SoftInputNumber
                  name={`monto-aprobacion-${idPago}`}
                  placeholder="Monto"
                  value={montoPagoAprobacion[idPago] ?? ""}
                  onChange={(event) => handleChangeMontoAprobacion(idPago, event.target.value)}
                />
                <SoftBox mt={1.5} display="flex" justifyContent="flex-end" gap={1.5} flexWrap="wrap">
                  <SoftButton
                    color="error"
                    circular
                    size="small"
                    onClick={() => handleRechazarPago(item)}
                    disabled={loadingSavePagoEstado}
                  >
                    Rechazar
                  </SoftButton>
                  <SoftButton
                    color="uproGreen"
                    circular
                    size="small"
                    onClick={() => handleAprobarPago(item)}
                    disabled={loadingSavePagoEstado}
                  >
                    {loadingSavePagoEstado ? <CircularProgress color="inherit" size={14} /> : "Aprobar"}
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            ) : null}
          </SoftBox>
        </Collapse>
      </Card>
    );
  };

  const loadingReclasificacion = loadingSavePedidoSegmento || loadingSavePagoSegmento || loadingSavePagoEstado;

  return (
    <Modal
      aria-labelledby="modal-cuenta-corriente-title"
      aria-describedby="modal-cuenta-corriente-description"
      open={open}
      onClose={handleCloseModal}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={modalStyle}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
              <SoftTypography variant="h6" id="modal-cuenta-corriente-title">
                {`Cuenta corriente ${selected?.nombre || ""} ${selected?.apellido || ""}`}
              </SoftTypography>
              <Tooltip title="Cerrar" placement="top">
                <Icon fontSize="medium" onClick={handleCloseModal} sx={{ cursor: "pointer" }}>
                  close
                </Icon>
              </Tooltip>
            </SoftBox>

            <SoftBox mt={2}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} md={5}>
                  <SoftBox display="flex" alignItems="flex-end" gap={2} sx={{ minWidth: 0 }}>
                    <SoftBox
                      sx={{
                        minWidth: 0,
                        overflow: "hidden",
                        opacity: mostrarFechaInicioPeriodo ? 1 : 0,
                        transform: mostrarFechaInicioPeriodo ? "translateX(0)" : "translateX(24px)",
                        width: mostrarFechaInicioPeriodo ? "50%" : "0px",
                        maxWidth: mostrarFechaInicioPeriodo ? "50%" : "0px",
                        flexBasis: mostrarFechaInicioPeriodo ? "50%" : "0px",
                        transition:
                          "width 250ms ease, max-width 250ms ease, flex-basis 250ms ease, opacity 200ms ease, transform 250ms ease",
                        pointerEvents: mostrarFechaInicioPeriodo ? "auto" : "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <SoftTypography variant="caption" fontWeight="bold">
                        Fecha inicio
                      </SoftTypography>
                      <SoftInput
                        type="date"
                        value={fechaInicioDraft}
                        onChange={(event) => setFechaInicioDraft(event.target.value)}
                      />
                    </SoftBox>
                    <SoftBox sx={{ flex: 1, minWidth: 0 }}>
                      <SoftTypography variant="caption" fontWeight="bold">
                        {modoSaldoDraft === MODO_SALDO_ACUMULADO ? "Fecha de corte" : "Fecha fin"}
                      </SoftTypography>
                      <SoftInput
                        type="date"
                        value={fechaFinDraft}
                        onChange={(event) => setFechaFinDraft(event.target.value)}
                      />
                    </SoftBox>
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Saldo a mostrar
                  </SoftTypography>
                  <Select
                    value={modoSaldoDraft}
                    onChange={handleChangeModoSaldo}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value={MODO_SALDO_ACUMULADO}>Saldo acumulado</MenuItem>
                    <MenuItem value={MODO_SALDO_PERIODO}>Saldo del período</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6} md={0.8}>
                  <SoftButton color="primary" iconOnly circular onClick={handleBuscar}>
                    <Icon>search</Icon>
                  </SoftButton>
                </Grid>
                <Grid item xs={12} sm={6} md={3.2}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Segmento
                  </SoftTypography>
                  <Select
                    value={segmentoFiltroDraft}
                    onChange={(event) => setSegmentoFiltroDraft(event.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value={SEGMENTO_TODOS}>Cuenta upro: Todos</MenuItem>
                    <MenuItem value={SEGMENTO_FUNCIONARIO}>Funcionario</MenuItem>
                    <MenuItem value={SEGMENTO_NO_FUNCIONARIO}>No funcionario</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </SoftBox>

            <SoftBox mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Card sx={{ p: 2, border: "1px solid #e6e6e6" }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon color="error">attach_money</Icon>
                      <SoftTypography variant="caption" color="text" fontWeight="bold">
                        Total deuda
                      </SoftTypography>
                    </SoftBox>
                    <SoftTypography variant="h6" color="error" mt={0.5}>
                      {loadingResumen ? "..." : valorAPesos(totalDeuda)}
                    </SoftTypography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Card sx={{ p: 2, border: "1px solid #e6e6e6" }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon color="success">payments</Icon>
                      <SoftTypography variant="caption" color="text" fontWeight="bold">
                        Total pagos aprobados
                      </SoftTypography>
                    </SoftBox>
                    <SoftTypography variant="h6" color="success" mt={0.5}>
                      {loadingResumen ? "..." : valorAPesos(totalPagos)}
                    </SoftTypography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Card sx={{ p: 2, border: "1px solid #e6e6e6", backgroundColor: "#FFF3E0" }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon color="warning">schedule</Icon>
                      <SoftTypography variant="caption" color="text" fontWeight="bold">
                        Pagos pendientes (Transferencia)
                      </SoftTypography>
                    </SoftBox>
                    <SoftTypography variant="h6" color="warning" mt={0.5}>
                      {loadingResumen ? "..." : `${cantidadPagosPendientes} pagos`}
                    </SoftTypography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Card sx={{ p: 2, border: "1px solid #e6e6e6", backgroundColor: "#FFEBEE" }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon color="error">cancel</Icon>
                      <SoftTypography variant="caption" color="text" fontWeight="bold">
                        Pagos rechazados (Transferencia)
                      </SoftTypography>
                    </SoftBox>
                    <SoftTypography variant="h6" color="error" mt={0.5}>
                      {loadingResumen ? "..." : `${cantidadPagosRechazados} pagos`}
                    </SoftTypography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Card sx={{ p: 2, border: "1px solid #e6e6e6" }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon color="info">redeem</Icon>
                      <SoftTypography variant="caption" color="text" fontWeight="bold">
                        Beca/Saldo recibido
                      </SoftTypography>
                    </SoftBox>
                    <SoftTypography variant="h6" color="info" mt={0.5}>
                      {loadingResumen ? "..." : valorAPesos(totalBecaSaldoRecibido)}
                    </SoftTypography>
                  </Card>
                </Grid>
                {modoSaldo !== MODO_SALDO_ACUMULADO ? (
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Card sx={{ p: 2, border: "1px solid #e6e6e6" }}>
                      <SoftBox display="flex" alignItems="center" gap={1}>
                        <Icon color="secondary">history</Icon>
                        <SoftTypography variant="caption" color="text" fontWeight="bold">
                          Saldo al inicio del período
                        </SoftTypography>
                      </SoftBox>
                      <SoftTypography variant="h6" color="secondary" mt={0.5}>
                        {loadingResumen ? "..." : valorAPesos(saldoInicioPeriodo)}
                      </SoftTypography>
                    </Card>
                  </Grid>
                ) : null}
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Card sx={{ p: 2, border: "1px solid #e6e6e6" }}>
                    <SoftBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ color: saldoCardMeta.color }}>account_balance_wallet</Icon>
                      <SoftTypography variant="caption" color="text" fontWeight="bold">
                        {saldoCardMeta.label}
                      </SoftTypography>
                    </SoftBox>
                    <SoftTypography
                      variant="h6"
                      sx={{ color: saldoCardMeta.color }}
                      mt={0.5}
                    >
                      {loadingResumen ? "..." : valorAPesos(saldoMostrado)}
                    </SoftTypography>
                  </Card>
                </Grid>
              </Grid>
            </SoftBox>

            {loadingReclasificacion ? (
              <SoftBox mt={2}>
                <Loading />
              </SoftBox>
            ) : null}

            <SoftBox mt={3}>
              <Grid container gap={2}>
                <Grid item xs={11} lg={6}>
                  <SoftTypography variant="h6" textAlign="center" mb={1}>
                    Pedidos en cuenta corriente
                  </SoftTypography>
                  <Divider sx={{ mb: 1.5 }} />

                  {loadingMovimientos ? (
                    <Loading />
                  ) : pedidos.length > 0 ? (
                    <SoftBox>
                      {pedidosPaginados.map(renderPedidoCard)}
                    </SoftBox>
                  ) : (
                    <SinDatos />
                  )}

                  <CustomPagination
                    loading={loadingMovimientos}
                    length={pedidosPaginados.length}
                    total={pedidosPagination.total}
                    pages={pedidosPagination.pages}
                    page={pedidosPage}
                    setPage={setPedidosPage}
                    noCard
                  />
                </Grid>

                <Grid item xs={11} lg={5} sx={{ borderLeft: { lg: "1px solid #e6e6e6" }, pl: { lg: 3 } }}>
                  <SoftTypography variant="h6" textAlign="center" mb={1}>
                    Pagos
                  </SoftTypography>
                  <Divider sx={{ mb: 1.5 }} />

                  {loadingMovimientos ? (
                    <Loading />
                  ) : pagosFiltrados.length > 0 ? (
                    <SoftBox>
                      {pagosPaginados.map(renderPagoCard)}
                    </SoftBox>
                  ) : (
                    <SinDatos />
                  )}

                  <CustomPagination
                    loading={loadingMovimientos}
                    length={pagosPaginados.length}
                    total={pagosPagination.total}
                    pages={pagosPagination.pages}
                    page={pagosPage}
                    setPage={setPagosPage}
                    noCard
                  />
                </Grid>
              </Grid>
            </SoftBox>
          </SoftBox>

          <Modal
            open={openModalFactura}
            onClose={handleCloseFacturaModal}
            closeAfterTransition
            aria-labelledby="modal-factura-title"
            aria-describedby="modal-factura-description"
          >
            <Fade in={openModalFactura}>
              <SoftBox
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "90%",
                  maxHeight: "92%",
                  overflowY: "auto",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  p: 2,
                }}
              >
                <Card>{facturaData && empresaData ? <FacturaImpresion factura={facturaData} empresa={empresaData} /> : null}</Card>
              </SoftBox>
            </Fade>
          </Modal>

          <Modal open={Boolean(previewFile)} onClose={() => setPreviewFile(null)} closeAfterTransition>
            <Fade in={Boolean(previewFile)}>
              <SoftBox
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "90%",
                  height: "90%",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  borderRadius: 2,
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <SoftTypography variant="h6">Comprobante</SoftTypography>
                  <Icon sx={{ cursor: "pointer" }} onClick={() => setPreviewFile(null)}>
                    close
                  </Icon>
                </SoftBox>
                <SoftBox sx={{ flex: 1, minHeight: 0 }}>
                  {previewFile ? (
                    <iframe
                      src={resolveStoredFileUrl(previewFile)}
                      style={{ width: "100%", height: "100%", border: "none", borderRadius: 8 }}
                      title="Comprobante"
                    />
                  ) : null}
                </SoftBox>
              </SoftBox>
            </Fade>
          </Modal>

          {pdfData ? (
            <SoftBox sx={{ display: "none" }}>
              <FileHistorial data={pdfData} onGeneratePdf={handlePdfGenerated} />
            </SoftBox>
          ) : null}
        </Card>
      </Fade>
    </Modal>
  );
}

CuentaCorrienteModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  selected: PropTypes.object,
};
